import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  templates,
  type ConsultationHistoryEntry,
  type ParameterGovernance,
  type TemplateContext,
} from '@/shared/api/resources';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { HistoryTable } from '@/shared/ui/HistoryTab';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { Skeleton } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { buttonClassName } from '@/shared/ui/buttonStyles';

import { fieldErrorOf } from '../shared/fieldError';

import { AVISO_AUTOAPROVACAO, autoriaDe } from './autoaprovacao';

/**
 * Pecas comuns das telas do endosso de aniversario e da renovacao (P-PAR-3): o aviso
 * de que nada roda, a situacao da versao com a ativacao por outra pessoa, as versoes,
 * o historico e a paleta "+ Inserir Tag".
 */

/** Aviso de que o parametro e so guardado: nenhuma rotina roda ate a homologacao. */
export function SemRotina({
  regras,
  children,
}: {
  regras: string[];
  children: string;
}) {
  return (
    <Alert tone="info" title="Parametro guardado, sem rotina automatica">
      {children} Regras pendentes: {regras.join(', ')}.
    </Alert>
  );
}

/** Situacao da versao: a em vigor e o rascunho, com a ativacao. */
export function SituacaoDaVersao({
  ativa,
  rascunho,
  podeAtivar,
  onAtivar,
}: {
  ativa: ParameterGovernance | null;
  rascunho: ParameterGovernance | null;
  podeAtivar: boolean;
  onAtivar: () => void;
}) {
  const autoria = autoriaDe(rascunho?.authored_by_you, rascunho?.may_self_approve);
  return (
    <Alert tone="info" title="Parametro versionado">
      <div className="flex flex-col gap-2">
        <p>
          Salvar grava o rascunho; outra pessoa, com a alcada, poe a versao em vigor com
          justificativa, e a anterior fica revogada.
        </p>
        <p>
          {ativa
            ? `Em vigor: versao ${ativa.version}, ativada por ${ativa.activated_label || 'nao informado'} em ${formatDateTime(ativa.activated_at)}.`
            : 'Nenhuma versao em vigor.'}{' '}
          {rascunho
            ? `Rascunho aberto: versao ${rascunho.version}, por ${rascunho.updated_label || rascunho.created_label || 'nao informado'}.`
            : 'Salvar abre um rascunho com o que esta na tela.'}
        </p>
        {rascunho && podeAtivar ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" onClick={onAtivar} disabled={autoria.barrada}>
              Ativar versao {rascunho.version}
            </Button>
            {autoria.barrada ? (
              <span className="text-2xs">
                Voce criou ou editou este rascunho. Outra pessoa com a alcada ativa a
                versao.
              </span>
            ) : null}
            {autoria.autoaprovacao ? (
              <span className="text-2xs">{AVISO_AUTOAPROVACAO}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </Alert>
  );
}

/** Dialogo da ativacao, com a justificativa obrigatoria. */
export function AtivarVersao({
  versao,
  onFechar,
  ativar,
  onAtivado,
}: {
  versao: number;
  onFechar: () => void;
  ativar: (justificativa: string) => Promise<unknown>;
  onAtivado: () => void;
}) {
  const [justificativa, setJustificativa] = useState('');
  const mutacao = useMutation({
    mutationFn: () => ativar(justificativa),
    onSuccess: onAtivado,
  });
  const apiError = mutacao.error instanceof ApiError ? mutacao.error : null;
  return (
    <Modal
      open
      onClose={onFechar}
      title={`Ativar versao ${versao}`}
      description="A versao em vigor passa a revogada, e esta entra em vigor a partir de agora."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          mutacao.mutate();
        }}
      >
        {mutacao.error && !fieldErrorOf(apiError, 'justification') ? (
          <Alert tone="danger" title="Nao foi possivel ativar">
            {mutacao.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Justificativa da ativacao"
          required
          maxLength={4000}
          value={justificativa}
          error={fieldErrorOf(apiError, 'justification')}
          onChange={(evento) => setJustificativa(evento.target.value)}
        />
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onFechar}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={mutacao.isPending}
            disabled={justificativa.trim() === ''}
          >
            Ativar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** As versoes do parametro: o rascunho, a ativa e as revogadas. */
export function VersoesDoParametro<T extends ParameterGovernance & { id: string }>({
  versoes,
  carregando,
  erro,
  resumo,
}: {
  versoes: T[];
  carregando: boolean;
  erro: Error | null;
  resumo?: ((versao: T) => string) | undefined;
}) {
  return (
    <DetailCard
      title="Versoes"
      description="Toda versao fica guardada: o rascunho, a ativa e as revogadas."
      loading={carregando}
      error={erro}
    >
      <DataTable
        columns={[
          {
            key: 'versao',
            header: 'Versao',
            render: (v: T) => `Versao ${v.version}`,
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (v: T) => <StatusBadge kind="versao" value={v.status} />,
          },
          ...(resumo
            ? [{ key: 'valores', header: 'Valores', render: (v: T) => resumo(v) }]
            : []),
          {
            key: 'aberta',
            header: 'Aberta por',
            hideBelow: 'md' as const,
            render: (v: T) => v.created_label || 'nao informado',
          },
          {
            key: 'ativada',
            header: 'Ativada por',
            hideBelow: 'md' as const,
            render: (v: T) =>
              v.activated_label
                ? `${v.activated_label} em ${formatDateTime(v.activated_at)}`
                : '-',
          },
          {
            key: 'motivo',
            header: 'Justificativa',
            render: (v: T) =>
              [v.justification, v.revocation_reason].filter(Boolean).join(' . ') || '-',
          },
        ]}
        items={versoes}
        keyOf={(v) => v.id}
        empty={<EmptyState icon="layers" title="Nenhuma versao ainda" />}
      />
    </DetailCard>
  );
}

const rotulosDaAcao: Record<string, string> = {
  'parametrizacao.aniversario.create': 'Rascunho da configuracao aberto',
  'parametrizacao.aniversario.update': 'Rascunho da configuracao alterado',
  'parametrizacao.aniversario.approve': 'Versao da configuracao ativada',
  'parametrizacao.aniversario.revoke': 'Versao da configuracao revogada',
  'parametrizacao.aniversario_modalidade.create': 'Rascunho da modalidade aberto',
  'parametrizacao.aniversario_modalidade.update': 'Rascunho da modalidade alterado',
  'parametrizacao.aniversario_modalidade.approve': 'Versao da modalidade ativada',
  'parametrizacao.aniversario_modalidade.revoke': 'Versao da modalidade revogada',
  'parametrizacao.renovacao.create': 'Rascunho da configuracao aberto',
  'parametrizacao.renovacao.update': 'Rascunho da configuracao alterado',
  'parametrizacao.renovacao.approve': 'Versao da configuracao ativada',
  'parametrizacao.renovacao.revoke': 'Versao da configuracao revogada',
  'parametrizacao.renovacao.perfil_incluido': 'Perfil incluido no aviso',
  'parametrizacao.renovacao.perfil_retirado': 'Perfil retirado do aviso',
  'parametrizacao.renovacao_modalidade.create': 'Rascunho da modalidade aberto',
  'parametrizacao.renovacao_modalidade.update': 'Rascunho da modalidade alterado',
  'parametrizacao.renovacao_modalidade.approve': 'Versao da modalidade ativada',
  'parametrizacao.renovacao_modalidade.revoke': 'Versao da modalidade revogada',
};

const rotulosDoCampo: Record<string, string> = {
  scope: 'Escopo',
  setting_id: 'Identificador da versao',
  version: 'Versao',
  status: 'Situacao',
  notes: 'Observacao',
  justification: 'Justificativa',
  revocation_reason: 'Motivo da revogacao',
  based_on_version: 'Copiada da versao',
  advance_notice_days: 'Notificacao previa do aniversario (dias)',
  boleto_due_days: 'Vencimento do boleto (dias)',
  rate_source: 'Taxa usada no endosso',
  notice_email_subject: 'Titulo do email de aviso',
  notice_email_message_characters: 'Mensagem do email de aviso (caracteres)',
  notice_email_message_checksum: 'Mensagem do email de aviso (soma de conferencia)',
  object_description_characters: 'Descricao do objeto (caracteres)',
  object_description_checksum: 'Descricao do objeto (soma de conferencia)',
  modality_id: 'Identificador da modalidade',
  modality_name: 'Modalidade',
  economic_index: 'Indice',
  notified_roles: 'Perfis avisados',
  notified_role_ids: 'Identificadores dos perfis avisados',
  role_id: 'Identificador do perfil',
  role_name: 'Perfil',
  max_term_days: 'Prazo maximo de vigencia (dias)',
  quote_lead_days: 'Prazo para criacao da cotacao (dias)',
  auto_cancel_after_days: 'Prazo para cancelamento automatico (dias)',
  issuance_deadline_days: 'Prazo para emissao da apolice (dias)',
  auto_cancel_enabled: 'Cancelamento automatico',
  auto_issue_enabled: 'Emissao automatica',
  bypass_available_limit: 'Dispensa do limite disponivel',
};

/** "Historico de alteracoes" do parametro, com o antes e o depois de cada mudanca. */
export function HistoricoDoParametro({
  chave,
  carregar,
  onClose,
}: {
  chave: readonly unknown[];
  carregar: (signal?: AbortSignal) => Promise<ConsultationHistoryEntry[]>;
  onClose: () => void;
}) {
  const historico = useQuery({
    queryKey: chave,
    queryFn: ({ signal }) => carregar(signal),
  });
  return (
    <Modal open onClose={onClose} title="Historico de alteracoes" size="lg">
      {historico.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : historico.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar o historico">
          {historico.error.message}
        </Alert>
      ) : (
        <HistoryTable
          rows={historico.data}
          emptyText="Nenhuma alteracao registrada"
          actionLabel={(acao) => rotulosDaAcao[acao] ?? acao}
          fieldLabel={(campo) => rotulosDoCampo[campo] ?? campo}
        />
      )}
    </Modal>
  );
}

/**
 * "+ Inserir Tag" do OnPoint: poe a marcacao da plataforma no texto. As opcoes sao o
 * vocabulario do motor de documento no contexto do texto (`email` no aviso, `objeto`
 * no objeto do endosso). O servidor confere de novo ao salvar.
 */
export function InserirTag({
  contexto,
  rotulo = '+ Inserir Tag',
  onIncluir,
  disabled,
}: {
  contexto: TemplateContext;
  rotulo?: string;
  onIncluir: (marcacao: string) => void;
  disabled: boolean;
}) {
  const vocabulario = useQuery({
    queryKey: ['template-variables', contexto],
    queryFn: ({ signal }) => templates.variables(contexto, signal),
    retry: false,
    staleTime: 5 * 60_000,
  });
  const opcoes = (vocabulario.data ?? [])
    .filter((variavel) => !variavel.list)
    .map((variavel) => ({
      value: variavel.name,
      label: `${variavel.description} (${variavel.name})`,
    }));
  return (
    <Select
      label={rotulo}
      options={opcoes}
      placeholder="Escolha a TAG para incluir"
      value=""
      disabled={disabled || opcoes.length === 0}
      hint={
        vocabulario.isError
          ? 'O vocabulario da plataforma nao abriu; escreva a TAG como {{grupo.nome}}.'
          : undefined
      }
      onChange={(evento) => {
        if (evento.target.value) onIncluir(`{{${evento.target.value}}}`);
      }}
    />
  );
}

/** Situacao da modalidade na lista: em vigor, rascunho ou os dois. */
export function SituacaoDaModalidade({
  ativa,
  rascunho,
}: {
  ativa: ParameterGovernance | null;
  rascunho: ParameterGovernance | null;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {ativa ? <StatusBadge kind="versao" value="ativa" /> : null}
      {rascunho ? <StatusBadge kind="versao" value="rascunho" /> : null}
    </div>
  );
}

/** Botao "Voltar", que leva a tela anterior da parametrizacao. */
export function Voltar({ para }: { para: string }) {
  return (
    <Link to={para} className={buttonClassName({ variant: 'secondary', size: 'md' })}>
      Voltar
    </Link>
  );
}
